import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, Search } from '@mui/icons-material';

// Run once as mandatory setup when the simulation is created; the backend keeps
// them available regardless of selection, so they can never be unchecked.
export const ALWAYS_ON_TOOLS = [
  'create_company',
  'create_deal_for_me',
  'create_contact',
  'add_stakeholder_to_deal',
];

const ToolRow = ({ tool, checked, locked, onToggle }) => (
  <Box
    display={'flex'}
    alignItems={'flex-start'}
    gap={1}
    py={0.5}
    borderBottom={'1px solid'}
    borderColor={'divider'}
  >
    <Checkbox
      size="small"
      checked={checked}
      disabled={locked}
      onChange={(e) => onToggle(tool?.name, e.target.checked)}
      sx={{ p: 0.5 }}
    />
    <Box flex={1}>
      <Box display={'flex'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
        <Typography variant={'body2'} fontWeight={'bold'}>
          {tool?.name}
        </Typography>
        {locked ? (
          <Typography variant={'caption'} color={'text.secondary'}>
            always included — required to set up the deal
          </Typography>
        ) : null}
      </Box>
      {tool?.description ? (
        <Typography
          variant={'caption'}
          color={'text.secondary'}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={tool?.description}
        >
          {tool?.description}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

const ToolsPicker = ({
  tools,
  fetching,
  error,
  restrict,
  onRestrictChange,
  selected,
  onSelectedChange,
}) => {
  const [keyword, setKeyword] = useState('');

  const trimmedKeyword = keyword.trim().toLowerCase();
  const matches = (tool) =>
    !trimmedKeyword ||
    tool?.name?.toLowerCase()?.includes(trimmedKeyword) ||
    tool?.description?.toLowerCase()?.includes(trimmedKeyword);

  const lockedTools = tools.filter((t) => ALWAYS_ON_TOOLS.includes(t?.name));
  const vettedTools = tools.filter(
    (t) => !ALWAYS_ON_TOOLS.includes(t?.name) && t?.source !== 'existing',
  );
  const unvettedTools = tools.filter(
    (t) => !ALWAYS_ON_TOOLS.includes(t?.name) && t?.source === 'existing',
  );

  const handleToggle = (name, checked) => {
    onSelectedChange(
      checked
        ? [...selected.filter((it) => it !== name), name]
        : selected.filter((it) => it !== name),
    );
  };

  const renderRows = (list, locked = false) =>
    list
      .filter(matches)
      .map((tool) => (
        <ToolRow
          key={tool?.name}
          tool={tool}
          locked={locked}
          checked={locked || selected.includes(tool?.name)}
          onToggle={handleToggle}
        />
      ));

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <FormControlLabel
        control={
          <Switch
            checked={restrict}
            onChange={(e) => onRestrictChange(e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography variant={'body2'} fontWeight={'bold'}>
              Restrict tools
            </Typography>
            <Typography variant={'caption'} color={'text.secondary'}>
              {restrict
                ? 'Agents can only use the tools checked below (plus the always-included setup tools).'
                : 'All tools — every tool in each agent’s role subset is available.'}
            </Typography>
          </Box>
        }
      />

      {!restrict ? null : fetching ? (
        <Box display={'flex'} alignItems={'center'} gap={1} py={3}>
          <CircularProgress size={20} />
          <Typography variant={'body2'} color={'text.secondary'}>
            Loading tools...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            gap={1}
          >
            <TextField
              size="small"
              placeholder="Search tools..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />
            <Typography variant={'caption'} color={'text.secondary'}>
              {selected.length} selected
            </Typography>
          </Box>

          {restrict && selected.length === 0 ? (
            <Alert severity="warning">
              No tools checked — agents will only have the always-included setup
              tools. Turn off “Restrict tools” to allow all tools.
            </Alert>
          ) : null}

          <Box>
            <Typography variant={'subtitle2'} fontWeight={'bold'} mb={0.5}>
              Simulation tools
            </Typography>
            {renderRows(lockedTools, true)}
            {renderRows(vettedTools)}
          </Box>

          {unvettedTools.length > 0 ? (
            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                  <Typography variant={'subtitle2'} fontWeight={'bold'}>
                    Platform tools
                  </Typography>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="not yet vetted for simulation use"
                  />
                  {unvettedTools.filter((t) => selected.includes(t?.name))
                    .length > 0 ? (
                    <Typography variant={'caption'} color={'text.secondary'}>
                      {
                        unvettedTools.filter((t) => selected.includes(t?.name))
                          .length
                      }{' '}
                      selected
                    </Typography>
                  ) : null}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant={'caption'}
                  color={'text.secondary'}
                  display={'block'}
                  mb={1}
                >
                  Some of these act through the seller’s real connected accounts
                  — enabling one is a genuine capability grant.
                </Typography>
                {renderRows(unvettedTools)}
              </AccordionDetails>
            </Accordion>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default ToolsPicker;
